import {
  createMissingContentError,
  getMissingContentIssues,
  shouldCheckForEmptyBlocks,
} from '@/cms/api/validateEntryContent';
import {
  CMSPageShellProps,
  SplitContentBlocks,
  TransformedCMSPageShellProps,
} from '@/cms/components/page-shell/types';
import { SideNavHashId, SideNavSection } from '@/cms/components/types';
import { Entry } from '@/cms/types/entry';
import { aggregatePageData } from '@/cms/utils';
import { normalizeHeadingForMatch } from '@/features/blog/SideNavigation/utils';

import { DecodoComponentKey } from '../components-decodo-list';
import { Block } from '../types';

export function validateEntryContent(entry: Entry) {
  const missingContentIssues = getMissingContentIssues(entry);

  if (missingContentIssues.length > 0 && shouldCheckForEmptyBlocks()) {
    throw createMissingContentError({
      identifier: entry.slug,
      issues: missingContentIssues,
    });
  }
}

export const preNavigableContentKeys: DecodoComponentKey[] = [
  'molecules.breadcrumbs',
  'hero.homepage-hero',
  'carousels.images-carousel',
  'shared.features-block',
  'hero.hero',
  'bento.scraping-hero',
  'bento.ai-solution-hero',
  'bento.image-carousel',
  'bento.feature-block',
  'bento.scraping-hero',

  // TODO: this will be the key to the newly redesigned hero components:
  // Keeping here for future reference.
  // 'hero.new-hero-scraper',
  // 'hero.new-hero-product-suggestion',
  // 'hero.new-features-block',
  // 'carousels.new-logo-carousel',
];

export const postNavigableContentKeys: DecodoComponentKey[] = [
  'layout.footer-banner',
  'bento.bottom-banner',
  'shared.top-banner-component',
  'molecules.banner',
  // TODO: same as preNavigableContentKeys
  // Keeping here for future reference.
  'layout.new-footer-banner',
];

function splitContentBlocksForSideNav(blocks: Block[]): SplitContentBlocks {
  const result = blocks.reduce(
    (acc, block) => {
      if (preNavigableContentKeys.includes(block.__component)) {
        acc.preNavigableContent.push(block);
      } else if (postNavigableContentKeys.includes(block.__component)) {
        acc.postNavigableContent.push(block);
      } else {
        acc.navigableContent.push(block);
      }
      return acc;
    },
    {
      preNavigableContent: [] as Block[],
      navigableContent: [] as Block[],
      postNavigableContent: [] as Block[],
    }
  );

  return result;
}

export function CMSPageShellTransform(
  props: CMSPageShellProps
): TransformedCMSPageShellProps {
  const {
    entry,
    pageData: pageDataProps,
    status,
    fetchOptions,
    envBridgeOptions,
  } = props;

  const pageData = { ...aggregatePageData(entry), ...pageDataProps };

  const envOptions = {
    status,
    fetchOptions,
    envBridgeOptions,
  };

  return {
    envOptions,
    entry,
    pageData,
    splitContentBlocks: splitContentBlocksForSideNav(entry.blocks),
  };
}

// this can probably be improved? seems too fragile simply checking for a #..
const isStringSideNavLinkId = (id: string): id is SideNavHashId => {
  return id.startsWith('#');
};

export function createNavigableIdForSideNavLink(
  section: SideNavSection
): SideNavHashId {
  const text = section.tag;

  if (isStringSideNavLinkId(text)) {
    return text;
  }

  return `#${normalizeHeadingForMatch(text)}`;
}

export function createNavigableIdForSectionHeading(
  section: SideNavSection
): string {
  const navigableId = createNavigableIdForSideNavLink(section);

  return navigableId.substring(1);
}

export function aggregateSideNavSections(
  blocks: SplitContentBlocks['navigableContent']
): SideNavSection[] {
  return blocks
    .map((block) => {
      const sliceHeadingAttributes = block.sliceHeading?.data?.attributes;

      if (!sliceHeadingAttributes?.showInSidenav) return null;
      if (!sliceHeadingAttributes?.tag) return null;

      return {
        tag: sliceHeadingAttributes.tag,
      };
    })
    .filter(Boolean) as SideNavSection[];
}
